/**
 * Reading Progress Service
 * 
 * Tracks reading positions for articles using localStorage.
 * Stores scroll position and last read paragraph for each article.
 */

const STORAGE_KEY = 'linkflare:reading_progress'

class ReadingProgressService {
  constructor() {
    this.progressData = this.loadFromStorage()
  }

  /**
   * Load all reading progress from localStorage
   */
  loadFromStorage() {
    try {
      const data = localStorage.getItem(STORAGE_KEY)
      return data ? JSON.parse(data) : {}
    } catch (error) {
      console.error('Failed to load reading progress:', error)
      return {}
    }
  }

  /**
   * Save all reading progress to localStorage
   */
  saveToStorage() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.progressData))
    } catch (error) {
      console.error('Failed to save reading progress:', error)
    }
  }

  /**
   * Save reading position for an article
   * @param {number} linkId - The article link ID
   * @param {object} position - Position data
   * @param {number} position.scrollTop - Scroll position
   * @param {number} position.scrollHeight - Total scroll height
   * @param {number} position.paragraphIndex - Index of current paragraph
   * @param {string} position.paragraphText - Text preview of current paragraph
   */
  savePosition(linkId, position) {
    if (!linkId) return

    this.progressData[linkId] = {
      ...position,
      timestamp: Date.now(),
      percentRead: position.scrollHeight > 0 
        ? Math.round((position.scrollTop / position.scrollHeight) * 100) 
        : 0
    }

    this.saveToStorage()
  }

  /**
   * Get reading position for an article
   * @param {number} linkId - The article link ID
   * @returns {object|null} Position data or null if not found
   */
  getPosition(linkId) {
    if (!linkId) return null
    return this.progressData[linkId] || null
  }

  /**
   * Delete reading position for an article
   * @param {number} linkId - The article link ID
   */
  deletePosition(linkId) {
    if (!linkId) return
    
    delete this.progressData[linkId]
    this.saveToStorage()
  }

  /**
   * Clear all reading positions
   */
  clearAll() {
    this.progressData = {}
    this.saveToStorage()
  }

  /**
   * Get all reading positions
   * @returns {object} All stored reading positions
   */
  getAllPositions() {
    return { ...this.progressData }
  }

  /**
   * Clean up old reading positions (older than 90 days)
   * @param {number} daysToKeep - Number of days to keep (default: 90)
   */
  cleanupOld(daysToKeep = 90) {
    const cutoffTime = Date.now() - (daysToKeep * 24 * 60 * 60 * 1000)
    let cleanedCount = 0

    Object.keys(this.progressData).forEach(linkId => {
      const position = this.progressData[linkId]
      if (position.timestamp < cutoffTime) {
        delete this.progressData[linkId]
        cleanedCount++
      }
    })

    if (cleanedCount > 0) {
      this.saveToStorage()
    }

    return cleanedCount
  }

  /**
   * Get reading progress statistics
   * @returns {object} Statistics about reading progress
   */
  getStats() {
    const positions = Object.values(this.progressData)
    
    if (positions.length === 0) {
      return {
        totalArticles: 0,
        averageProgress: 0,
        articlesCompleted: 0
      }
    }

    const totalProgress = positions.reduce((sum, pos) => sum + (pos.percentRead || 0), 0)
    const articlesCompleted = positions.filter(pos => (pos.percentRead || 0) >= 99).length

    return {
      totalArticles: positions.length,
      averageProgress: Math.round(totalProgress / positions.length),
      articlesCompleted
    }
  }
}

export default new ReadingProgressService()
